// Client Portal — Email Intelligence review console (Phase 5).
// Port of the approved mockup client-portal/email-review-console.html to the
// portal's Tailwind + orange-accent conventions. Three panes: priority triage
// queue (chips from stored scores), eval rubric + routing reason + masked
// email, and the draft with its sentence->source grounding map and evidence.
// The console renders stored facts — it never re-scores.
import { useCallback, useEffect, useState } from "react";
import { emailIntelFetch } from "../lib/api";

const ACTION_META = {
  auto_draft: { label: "Auto-draft", cls: "bg-green-100 text-green-700" },
  escalate: { label: "Escalate", cls: "bg-red-100 text-red-700" },
  hold_teach: { label: "Hold & teach", cls: "bg-amber-100 text-amber-700" },
  human_no_draft: {
    label: "Human, no draft",
    cls: "bg-amber-100 text-amber-700",
  },
  auto_archive: { label: "Auto-archive", cls: "bg-slate-200 text-slate-600" },
};

const prioDisplay = (p) => Math.round(Math.min(p ?? 0.5, 2) * 50);
const prioCls = (p) =>
  prioDisplay(p) >= 70
    ? "bg-red-100 text-red-700"
    : prioDisplay(p) >= 45
      ? "bg-amber-100 text-amber-700"
      : "bg-slate-200 text-slate-600";

// Render masked text with [EMAIL_1]-style tokens as redaction chips.
function MaskedText({ text }) {
  const parts = String(text || "").split(/(\[[A-Z]+_\d+\])/g);
  return (
    <>
      {parts.map((p, i) =>
        /^\[[A-Z]+_\d+\]$/.test(p) ? (
          <span
            key={i}
            className="font-mono text-[10px] px-1 py-0.5 rounded bg-slate-200 text-slate-500 align-middle"
          >
            {p.slice(1, -1)}
          </span>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

function Meter({ value, tone }) {
  const cls =
    tone === "ok"
      ? "bg-green-500"
      : tone === "warn"
        ? "bg-amber-500"
        : "bg-red-500";
  return (
    <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
      <div
        className={`h-full rounded-full ${cls}`}
        style={{
          width: `${Math.round(Math.min(Math.max(value, 0), 1) * 100)}%`,
        }}
      />
    </div>
  );
}

function RubricPanel({ scores, decision, thresholds }) {
  const byDim = {};
  scores.forEach((s) => {
    byDim[s.dimension] = s; // rows arrive oldest-first; latest wins
  });
  const intent = byDim.intent;
  const urgency = byDim.urgency;
  const sentiment = byDim.sentiment;
  const coverage = byDim.coverage;
  const covThreshold =
    coverage?.raw_output?.threshold ?? thresholds?.coverage ?? 0.55;

  return (
    <div className="px-4 py-3.5 border-b border-slate-100">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
          Evaluation
        </span>
        {decision && (
          <span
            className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${ACTION_META[decision.action]?.cls || ""}`}
          >
            → {ACTION_META[decision.action]?.label || decision.action}
            {decision.matched_rule_id ? "" : " (fallthrough)"}
          </span>
        )}
      </div>
      <div className="space-y-3">
        {intent && (
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="font-semibold text-slate-700">Intent</span>
              <span className="text-slate-500">
                <b className="text-slate-800 capitalize">
                  {intent.label || "—"}
                </b>
                {" · "}
                {Number(intent.score).toFixed(2)}
              </span>
            </div>
          </div>
        )}
        {urgency && (
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="font-semibold text-slate-700">Urgency</span>
              <span className="text-slate-500">
                <b className="text-slate-800">
                  {Number(urgency.score).toFixed(2)}
                </b>
                {urgency.raw_output?.churn_risk && " · churn risk"}
              </span>
            </div>
            <Meter
              value={urgency.score}
              tone={
                urgency.score >= 0.7
                  ? "err"
                  : urgency.score >= 0.4
                    ? "warn"
                    : "ok"
              }
            />
          </div>
        )}
        {sentiment && (
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="font-semibold text-slate-700">Sentiment</span>
              <span className="text-slate-500">
                <b className="text-slate-800 capitalize">
                  {sentiment.label || "—"}
                </b>
              </span>
            </div>
            <Meter
              value={1 - sentiment.score}
              tone={sentiment.score <= 0.25 ? "err" : "ok"}
            />
          </div>
        )}
        {coverage && (
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="font-semibold text-slate-700">KB coverage</span>
              <span className="text-slate-500">
                <b className="text-slate-800">
                  {Number(coverage.score).toFixed(2)}
                </b>
                {" · "}
                {coverage.score >= covThreshold ? "answerable" : "KB gap"}
              </span>
            </div>
            <Meter
              value={coverage.score}
              tone={coverage.score >= covThreshold ? "ok" : "warn"}
            />
          </div>
        )}
      </div>
      {decision?.reason && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5">
            Why it routed here
          </p>
          <div className="flex flex-wrap gap-1.5">
            {decision.matched_rule_id ? (
              decision.reason.map((r, i) => (
                <span
                  key={i}
                  className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600"
                >
                  {r.cond?.dimension} {r.cond?.op} {String(r.cond?.value)} · got{" "}
                  {typeof r.actual === "number"
                    ? r.actual.toFixed(2)
                    : String(r.actual)}
                  {r.passed ? " ✓" : " ✗"}
                </span>
              ))
            ) : (
              <span className="text-[10px] text-slate-500">
                No rule matched — fallthrough
                {decision.reason[0]?.note ? `: ${decision.reason[0].note}` : ""}
                {decision.reason[0]?.budget_exceeded &&
                  " (daily eval budget exceeded)"}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DraftPane({ detail, facilityId, onResolved, onRefresh }) {
  const { item, scores, kb_gap: kbGap } = detail;
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [gapAnswer, setGapAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showCriteria, setShowCriteria] = useState(false);
  const [highlightChunk, setHighlightChunk] = useState(null);

  const selfEvals = scores.filter((s) => s.dimension === "self_eval");
  const selfEval = selfEvals[selfEvals.length - 1];
  const coverage = scores.find((s) => s.dimension === "coverage");
  const chunks = coverage?.raw_output?.chunks || [];
  const grounding = selfEval?.raw_output?.grounding;
  const sentences =
    grounding?.map((g) => ({
      text: g.sentence,
      grounded: g.grounded,
      chunk_ids: g.chunk_ids || [],
    })) ||
    (item.payload?.sentence_sources || []).map((s) => ({
      text: s.text,
      grounded: (s.chunk_ids || []).length > 0,
      chunk_ids: s.chunk_ids || [],
    }));
  const unverified = sentences.filter((s) => !s.grounded).length;
  const draftText = item.payload?.draft_text || "";

  const act = async (action, extra = {}) => {
    setBusy(true);
    setError(null);
    try {
      await emailIntelFetch(`/console/items/${item.id}/action`, {
        method: "POST",
        body: JSON.stringify({ facility_id: facilityId, action, ...extra }),
      });
      onResolved();
    } catch (e) {
      setError("Action failed.");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setBusy(true);
    setError(null);
    try {
      await emailIntelFetch(`/console/items/${item.id}/regenerate`, {
        method: "POST",
        body: JSON.stringify({ facility_id: facilityId }),
      });
      onRefresh();
    } catch (e) {
      setError("Regenerate failed.");
    } finally {
      setBusy(false);
    }
  };

  const answerGap = async () => {
    setBusy(true);
    setError(null);
    try {
      await emailIntelFetch(`/console/kb-gaps/${kbGap.id}/answer`, {
        method: "POST",
        body: JSON.stringify({ facility_id: facilityId, answer: gapAnswer }),
      });
      setGapAnswer("");
      onRefresh();
    } catch (e) {
      setError("Could not save the answer.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col min-h-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
          Suggested draft
        </span>
        {selfEval && (
          <button
            type="button"
            onClick={() => setShowCriteria((v) => !v)}
            className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${
              selfEval.score >= (selfEval.raw_output?.min_queue_score ?? 0.7)
                ? "bg-green-100 text-green-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            ● Self-eval {Number(selfEval.score).toFixed(2)}{" "}
            {showCriteria ? "▴" : "▾"}
          </button>
        )}
      </div>
      {showCriteria && selfEval && (
        <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
          {(selfEval.raw_output?.criteria || []).map((c) => (
            <div key={c.name} className="flex justify-between text-xs py-0.5">
              <span className="text-slate-600">{c.name}</span>
              <span className="font-semibold text-slate-800">
                {Number(c.score).toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="px-4 py-3.5 flex-1">
        {editing ? (
          <>
            <textarea
              className="w-full text-sm leading-relaxed border border-slate-200 rounded-lg p-3 min-h-[160px] outline-none focus:border-orange-400"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
            />
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                disabled={busy || !editText.trim()}
                onClick={() => act("edit", { edited_draft: editText })}
                className="px-3 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 disabled:opacity-50"
              >
                Save edit & resolve
              </button>
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-xs font-semibold"
              >
                Cancel
              </button>
            </div>
          </>
        ) : draftText ? (
          <p className="text-sm leading-7 text-slate-700">
            {sentences.length > 0
              ? sentences.map((s, i) => (
                  <span key={i}>
                    <button
                      type="button"
                      onClick={() => setHighlightChunk(s.chunk_ids[0] || null)}
                      className={`text-left ${
                        s.grounded
                          ? "border-b-2 border-green-400 hover:bg-green-50"
                          : "bg-amber-50 border-b-2 border-dashed border-amber-400 px-0.5"
                      }`}
                    >
                      {s.text}
                    </button>{" "}
                  </span>
                ))
              : draftText}
          </p>
        ) : (
          <div className="text-sm text-slate-400 py-6 text-center">
            No draft
            {item.payload?.downgraded_from &&
              " — self-eval kept it below the bar"}
            {item.payload?.budget_exceeded &&
              " — daily eval budget was exceeded"}
            . A human handles this one.
          </div>
        )}

        {chunks.length > 0 && !editing && (
          <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden bg-slate-50/60">
            <div className="px-3 py-2 text-[10px] uppercase tracking-wide font-semibold text-slate-400 border-b border-slate-100">
              Grounded in · {chunks.length} retrieved chunks
            </div>
            {chunks.map((c) => (
              <div
                key={c.id}
                className={`px-3 py-2 border-b border-slate-100 last:border-b-0 ${highlightChunk === c.id ? "bg-orange-50" : ""}`}
              >
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
                    {c.source || "KB"}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400">
                    {c.id}
                  </span>
                  <span className="ml-auto text-[10px] text-slate-400">
                    sim {Number(c.similarity).toFixed(2)}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  {c.text}
                </p>
              </div>
            ))}
          </div>
        )}

        {kbGap && (
          <div className="mt-4 border border-amber-300 bg-amber-50 rounded-lg p-3">
            <p className="text-xs font-bold text-amber-800 mb-1">
              KB gap — answer once to teach
            </p>
            <p className="text-[11px] text-amber-700 mb-2">
              The KB couldn't answer: <i>"{kbGap.query.slice(0, 140)}"</i>{" "}
              (coverage {Number(kbGap.coverage ?? 0).toFixed(2)}). Your answer
              joins the knowledge base so next time it drafts automatically.
            </p>
            <textarea
              className="w-full text-xs border border-amber-200 rounded-lg p-2 min-h-[70px] outline-none focus:border-amber-400 bg-white"
              placeholder="Write the answer once…"
              value={gapAnswer}
              onChange={(e) => setGapAnswer(e.target.value)}
            />
            <button
              type="button"
              disabled={busy || !gapAnswer.trim()}
              onClick={answerGap}
              className="mt-2 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
            >
              Answer & teach the KB
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50/60">
        {unverified > 0 && draftText && !editing && (
          <span className="text-[11px] font-semibold text-amber-600">
            ⚠ {unverified} unverified {unverified === 1 ? "claim" : "claims"}
          </span>
        )}
        {error && <span className="text-[11px] text-red-600">{error}</span>}
        <div className="flex-1" />
        <button
          type="button"
          disabled={busy}
          onClick={regenerate}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-40"
        >
          Regenerate
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setEditText(draftText);
            setEditing(true);
          }}
          className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"
        >
          Edit
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => act("escalate")}
          className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"
        >
          Escalate
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => act("dismiss")}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-400 hover:text-slate-600 disabled:opacity-40"
        >
          Dismiss
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => act("approve")}
          className="px-3 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700 disabled:opacity-40"
        >
          Approve
        </button>
      </div>
    </div>
  );
}

export function EmailReviewConsole({ facility }) {
  const facilityId = facility.facility_id;
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadQueue = useCallback(async () => {
    try {
      const data = await emailIntelFetch(
        `/console/queue?facility_id=${facilityId}`,
      );
      setItems(data.items || []);
      setStats(data.stats || null);
      return data.items || [];
    } catch (e) {
      setError("Could not load the review queue.");
      return [];
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  const loadDetail = useCallback(
    async (itemId) => {
      try {
        const data = await emailIntelFetch(
          `/console/items/${itemId}?facility_id=${facilityId}`,
        );
        setDetail(data);
      } catch (e) {
        setError("Could not load that item.");
      }
    },
    [facilityId],
  );

  useEffect(() => {
    loadQueue().then((list) => {
      if (list.length > 0) {
        setSelectedId(list[0].id);
        loadDetail(list[0].id);
      }
    });
  }, [loadQueue, loadDetail]);

  const select = (id) => {
    setSelectedId(id);
    setDetail(null);
    loadDetail(id);
  };

  const onResolved = async () => {
    const list = await loadQueue();
    if (list.length > 0) {
      select(list[0].id);
    } else {
      setSelectedId(null);
      setDetail(null);
    }
  };

  const onRefresh = async () => {
    await loadQueue();
    if (selectedId) loadDetail(selectedId);
  };

  if (loading)
    return (
      <div className="text-center py-12 text-slate-400">
        Loading review queue…
      </div>
    );

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="font-semibold text-slate-700 text-sm">Review console</h3>
        {stats && (
          <div className="flex gap-5 text-right">
            <div>
              <p className="text-sm font-bold text-slate-800">
                {stats.in_queue}
              </p>
              <p className="text-[9px] uppercase tracking-wide text-slate-400">
                In queue
              </p>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800">
                {stats.auto_draftable_pct}%
              </p>
              <p className="text-[9px] uppercase tracking-wide text-slate-400">
                Auto-draftable
              </p>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800">
                {stats.kb_gaps_open}
              </p>
              <p className="text-[9px] uppercase tracking-wide text-slate-400">
                KB gaps
              </p>
            </div>
          </div>
        )}
      </div>
      {error && <p className="px-4 py-2 text-xs text-red-600">{error}</p>}

      {items.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-400">
          Queue is clear — nothing waiting for review.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_1fr] min-h-[560px]">
          {/* QUEUE */}
          <div className="border-r border-slate-100 bg-slate-50/60 overflow-y-auto max-h-[720px]">
            <div className="px-3.5 py-2.5 text-[10px] uppercase tracking-wide font-semibold text-slate-400 border-b border-slate-100 sticky top-0 bg-slate-50">
              Triage · by priority
            </div>
            {items.map((item) => {
              const cov = item.scores?.coverage?.score;
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => select(item.id)}
                  className={`block w-full text-left px-3.5 py-2.5 border-b border-slate-100 border-l-2 transition-colors ${
                    selectedId === item.id
                      ? "bg-white border-l-orange-500"
                      : "border-l-transparent hover:bg-slate-100/60"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`w-6 h-6 rounded text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${prioCls(item.priority)}`}
                    >
                      {prioDisplay(item.priority)}
                    </span>
                    <span className="text-xs font-semibold text-slate-700 truncate">
                      <MaskedText
                        text={item.payload?.from || "unknown sender"}
                      />
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mb-1.5">
                    <MaskedText
                      text={item.payload?.subject || "(no subject)"}
                    />
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {item.scores?.intent?.label && (
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 capitalize">
                        {item.scores.intent.label}
                      </span>
                    )}
                    {typeof cov === "number" && (
                      <span
                        className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${cov >= 0.55 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
                      >
                        Cov {cov.toFixed(2)}
                      </span>
                    )}
                    <span
                      className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${ACTION_META[item.action]?.cls || ""}`}
                    >
                      {ACTION_META[item.action]?.label || item.action}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* CENTER: eval + email */}
          <div className="border-r border-slate-100 overflow-y-auto max-h-[720px]">
            {detail ? (
              <>
                <div className="px-4 pt-4 pb-3 border-b border-slate-100">
                  <h2 className="text-base font-bold text-slate-800 mb-1">
                    <MaskedText
                      text={detail.item.payload?.subject || "(no subject)"}
                    />
                  </h2>
                  <p className="text-xs text-slate-500">
                    <MaskedText text={detail.item.payload?.from || ""} />
                    {" · "}
                    <span className="font-mono text-[10px]">
                      {detail.item.email_id}
                    </span>
                  </p>
                </div>
                <RubricPanel
                  scores={detail.scores}
                  decision={detail.decision}
                />
                <div className="px-4 py-3.5 text-sm leading-relaxed text-slate-600">
                  <MaskedText
                    text={
                      detail.item.payload?.body_text ||
                      detail.item.payload?.excerpt ||
                      ""
                    }
                  />
                </div>
              </>
            ) : (
              <div className="py-16 text-center text-sm text-slate-400">
                Loading…
              </div>
            )}
          </div>

          {/* RIGHT: draft + grounding + actions */}
          <div className="overflow-y-auto max-h-[720px]">
            {detail ? (
              <DraftPane
                detail={detail}
                facilityId={facilityId}
                onResolved={onResolved}
                onRefresh={onRefresh}
              />
            ) : (
              <div className="py-16 text-center text-sm text-slate-400">
                Loading…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
